package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminNotificationEventCode
import org.gymly.model.AdminNotificationScheduleFrequency
import org.gymly.model.AdminSettingsNotificationRule
import org.gymly.model.AdminSettingsNotificationRuleUpsert
import org.gymly.model.AdminSettingsNotificationSchedule
import org.gymly.model.AdminSettingsNotificationScheduleUpsert
import org.gymly.model.AdminSettingsNotificationTemplate
import org.gymly.model.AdminSettingsNotificationTemplateUpsert
import org.gymly.theme.AdminColors
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.userMessage

@Composable
internal fun ColumnScope.SettingsNotificationsPanel(compact: Boolean) {
    val scope = rememberCoroutineScope()
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var savingSchedule by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var success by remember { mutableStateOf<String?>(null) }
    var rules by remember { mutableStateOf<List<AdminSettingsNotificationRule>>(emptyList()) }
    var schedules by remember { mutableStateOf<List<AdminSettingsNotificationSchedule>>(emptyList()) }
    var templates by remember { mutableStateOf<List<AdminSettingsNotificationTemplate>>(emptyList()) }
    var selectedTemplateEvent by remember { mutableStateOf(AdminNotificationEventCode.NEW_CLIENT_REGISTERED) }
    var templateSubject by remember { mutableStateOf("") }
    var templateEmailBody by remember { mutableStateOf("") }
    var templateWebTitle by remember { mutableStateOf("") }
    var templateWebBody by remember { mutableStateOf("") }
    var variablesHelp by remember { mutableStateOf("") }
    var savingTemplate by remember { mutableStateOf(false) }

    fun loadTemplateFields(template: AdminSettingsNotificationTemplate) {
        templateSubject = template.emailSubject
        templateEmailBody = template.emailBodyText
        templateWebTitle = template.webTitle
        templateWebBody = template.webBody
    }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            success = null
            try {
                rules = AdminRepo.api.listSettingsNotificationRules()
                schedules = AdminRepo.api.listSettingsNotificationSchedules()
                templates = AdminRepo.api.listSettingsNotificationTemplates()
                if (variablesHelp.isBlank()) {
                    variablesHelp = AdminRepo.api.notificationTemplateVariablesHelp()
                }
                templates.firstOrNull { it.eventCode == selectedTemplateEvent }?.let { loadTemplateFields(it) }
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar las reglas de notificación.")
            } finally {
                loading = false
            }
        }
    }

    fun selectTemplateEvent(event: AdminNotificationEventCode) {
        selectedTemplateEvent = event
        templates.firstOrNull { it.eventCode == event }?.let { loadTemplateFields(it) }
    }

    LaunchedEffect(Unit) { reload() }

    fun updateRule(id: String, mutate: (AdminSettingsNotificationRule) -> AdminSettingsNotificationRule) {
        rules = rules.map { if (it.id == id) mutate(it) else it }
    }

    fun updateSchedule(
        event: AdminNotificationEventCode,
        mutate: (AdminSettingsNotificationSchedule) -> AdminSettingsNotificationSchedule,
    ) {
        schedules = schedules.map { if (it.eventCode == event) mutate(it) else it }
    }

    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(if (compact) 16.dp else 24.dp)
            // Un solo scroll para toda la pestaña: reglas, horarios y plantillas.
            .verticalScroll(rememberScrollState()),
    ) {
        Text(
            "Canales por evento, y también cuándo salen las notificaciones programadas (hora, diaria o por días).",
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
        )
        Spacer(Modifier.height(14.dp))
        error?.let {
            Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
        }
        success?.let {
            Text(it, color = AdminColors.Success, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
        }

        when {
            loading -> Box(Modifier.fillMaxWidth().padding(40.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            else -> {
                NotificationTimingSection(
                    schedules = schedules,
                    compact = compact,
                    onUpdate = { event, mutate -> updateSchedule(event, mutate) },
                    saving = savingSchedule,
                    onSave = {
                        scope.launch {
                            savingSchedule = true
                            error = null
                            success = null
                            try {
                                schedules = AdminRepo.api.replaceSettingsNotificationSchedules(
                                    schedules.map {
                                        AdminSettingsNotificationScheduleUpsert(
                                            eventCode = it.eventCode,
                                            enabled = it.enabled,
                                            frequency = it.frequency,
                                            sendHour = it.sendHour,
                                            mondayEnabled = it.mondayEnabled,
                                            tuesdayEnabled = it.tuesdayEnabled,
                                            wednesdayEnabled = it.wednesdayEnabled,
                                            thursdayEnabled = it.thursdayEnabled,
                                            fridayEnabled = it.fridayEnabled,
                                            saturdayEnabled = it.saturdayEnabled,
                                            sundayEnabled = it.sundayEnabled,
                                            leadDays = it.leadDays,
                                            timezone = it.timezone,
                                            inactivateAfterDays = it.inactivateAfterDays,
                                        )
                                    },
                                )
                                success = "Horarios de notificación guardados."
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudieron guardar los horarios.")
                            } finally {
                                savingSchedule = false
                            }
                        }
                    },
                )

                Spacer(Modifier.height(20.dp))
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
                Spacer(Modifier.height(16.dp))

                Text("Canales por evento", fontWeight = FontWeight.Bold, fontSize = 15.sp)
                Text(
                    "Elige dónde notifica cada evento: correo, push de la app móvil o el panel web.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                Spacer(Modifier.height(12.dp))

                if (rules.isEmpty()) {
                    Text("No hay reglas configuradas.", color = AdminColors.TextMuted)
                } else if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        rules.forEach { rule ->
                            NotificationRuleCard(rule) { email, push, web ->
                                updateRule(rule.id) {
                                    it.copy(emailEnabled = email, pushEnabled = push, webEnabled = web)
                                }
                            }
                        }
                    }
                } else {
                    NotificationRulesTable(
                        rules,
                        modifier = Modifier.fillMaxWidth(),
                    ) { id, email, push, web ->
                        updateRule(id) { it.copy(emailEnabled = email, pushEnabled = push, webEnabled = web) }
                    }
                }

                if (rules.isNotEmpty()) {
                    Spacer(Modifier.height(16.dp))
                    Button(
                        onClick = {
                            scope.launch {
                                saving = true
                                error = null
                                success = null
                                try {
                                    rules = AdminRepo.api.replaceSettingsNotificationRules(
                                        rules.map {
                                            AdminSettingsNotificationRuleUpsert(
                                                eventCode = it.eventCode,
                                                emailEnabled = it.emailEnabled,
                                                pushEnabled = it.pushEnabled,
                                                webEnabled = it.webEnabled,
                                            )
                                        },
                                    )
                                    success = "Reglas de notificación guardadas."
                                } catch (e: Exception) {
                                    error = e.userMessage("No se pudo guardar.")
                                } finally {
                                    saving = false
                                }
                            }
                        },
                        enabled = !saving,
                        modifier = if (compact) Modifier.fillMaxWidth().height(44.dp) else Modifier,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text(if (saving) "…" else "Guardar canales", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }

        if (!loading) {
            Spacer(Modifier.height(24.dp))
            HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
            Spacer(Modifier.height(16.dp))
            Column(Modifier.fillMaxWidth(),
        ) {
                Text("Plantillas de notificación", fontWeight = FontWeight.Bold, fontSize = 15.sp)
            Text(
                "Personaliza el asunto y el contenido del correo y del aviso web. Se incluyen el logo de tu empresa y la marca Gymly.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp, bottom = 12.dp),
            )
            NotificationTemplateEditor(
                compact = compact,
                templates = templates,
                selectedEvent = selectedTemplateEvent,
                subject = templateSubject,
                emailBody = templateEmailBody,
                webTitle = templateWebTitle,
                webBody = templateWebBody,
                variablesHelp = variablesHelp,
                saving = savingTemplate,
                onSelectEvent = { selectTemplateEvent(it) },
                onSubject = { templateSubject = it },
                onEmailBody = { templateEmailBody = it },
                onWebTitle = { templateWebTitle = it },
                onWebBody = { templateWebBody = it },
                onSave = {
                    scope.launch {
                        savingTemplate = true
                        error = null
                        success = null
                        try {
                            val saved = AdminRepo.api.upsertSettingsNotificationTemplate(
                                AdminSettingsNotificationTemplateUpsert(
                                    eventCode = selectedTemplateEvent,
                                    emailSubject = templateSubject,
                                    emailBodyText = templateEmailBody,
                                    webTitle = templateWebTitle,
                                    webBody = templateWebBody,
                                ),
                            )
                            templates = templates.map { if (it.eventCode == saved.eventCode) saved else it }
                            loadTemplateFields(saved)
                            success = "Plantilla guardada."
                        } catch (e: Exception) {
                            error = e.userMessage("No se pudo guardar la plantilla.")
                        } finally {
                            savingTemplate = false
                        }
                    }
                },
                onReset = {
                    scope.launch {
                        savingTemplate = true
                        error = null
                        success = null
                        try {
                            val saved = AdminRepo.api.resetSettingsNotificationTemplate(selectedTemplateEvent)
                            templates = templates.map { if (it.eventCode == saved.eventCode) saved else it }
                            loadTemplateFields(saved)
                            success = "Plantilla restaurada a los valores por defecto."
                        } catch (e: Exception) {
                            error = e.userMessage("No se pudo restaurar la plantilla.")
                        } finally {
                            savingTemplate = false
                        }
                    }
                },
            )
            }
        }
    }
}

/** Ancho fijo de la tabla de canales: el nombre del evento ocupa lo que sobra y, si no cabe, se desplaza. */
private val RULES_TABLE_WIDTH = 480.dp

@Composable
private fun NotificationRulesTable(
    rules: List<AdminSettingsNotificationRule>,
    modifier: Modifier = Modifier,
    onChange: (id: String, email: Boolean, push: Boolean, web: Boolean) -> Unit,
) {
    BoxWithConstraints(modifier.fillMaxWidth()) {
        // Si cabe en la tarjeta ocupa todo el ancho; si no, la tabla se desplaza en horizontal.
        val fits = maxWidth >= RULES_TABLE_WIDTH
        val rowWidth = if (fits) Modifier.fillMaxWidth() else Modifier.width(RULES_TABLE_WIDTH)
    Column(Modifier.fillMaxWidth().then(if (fits) Modifier else Modifier.horizontalScroll(rememberScrollState()))) {
        Row(
            rowWidth.padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminListHeaderLabel("Evento", Modifier.weight(1f))
            AdminListHeaderLabel("Correo", Modifier.width(90.dp))
            AdminListHeaderLabel("Push móvil", Modifier.width(100.dp))
            AdminListHeaderLabel("Web", Modifier.width(80.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        Column(Modifier.fillMaxWidth(),
        ) {
            rules.forEach { rule ->
                Row(
                    rowWidth.padding(vertical = 4.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(rule.eventLabel, Modifier.weight(1f), color = AdminColors.Text, fontSize = 13.sp)
                    Box(Modifier.width(90.dp)) {
                        Checkbox(
                            checked = rule.emailEnabled,
                            onCheckedChange = { onChange(rule.id, it, rule.pushEnabled, rule.webEnabled) },
                            colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                        )
                    }
                    Box(Modifier.width(100.dp)) {
                        Checkbox(
                            checked = rule.pushEnabled,
                            onCheckedChange = { onChange(rule.id, rule.emailEnabled, it, rule.webEnabled) },
                            colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                        )
                    }
                    Box(Modifier.width(80.dp)) {
                        Checkbox(
                            checked = rule.webEnabled,
                            onCheckedChange = { onChange(rule.id, rule.emailEnabled, rule.pushEnabled, it) },
                            colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                        )
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.3f))
            }
        }
    }
    }
}

@Composable
private fun NotificationRuleCard(
    rule: AdminSettingsNotificationRule,
    onChange: (email: Boolean, push: Boolean, web: Boolean) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(12.dp),
    ) {
        Text(rule.eventLabel, fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 13.sp)
        Spacer(Modifier.height(8.dp))
        ChannelCheck("Correo", rule.emailEnabled) { onChange(it, rule.pushEnabled, rule.webEnabled) }
        ChannelCheck("Push móvil", rule.pushEnabled) { onChange(rule.emailEnabled, it, rule.webEnabled) }
        ChannelCheck("Web", rule.webEnabled) { onChange(rule.emailEnabled, rule.pushEnabled, it) }
    }
}

@Composable
private fun NotificationTemplateEditor(
    compact: Boolean,
    templates: List<AdminSettingsNotificationTemplate>,
    selectedEvent: AdminNotificationEventCode,
    subject: String,
    emailBody: String,
    webTitle: String,
    webBody: String,
    variablesHelp: String,
    saving: Boolean,
    onSelectEvent: (AdminNotificationEventCode) -> Unit,
    onSubject: (String) -> Unit,
    onEmailBody: (String) -> Unit,
    onWebTitle: (String) -> Unit,
    onWebBody: (String) -> Unit,
    onSave: () -> Unit,
    onReset: () -> Unit,
) {
    var eventMenu by remember { mutableStateOf(false) }
    val eventLabel = templates.firstOrNull { it.eventCode == selectedEvent }?.eventLabel
        ?: selectedEvent.labelEs()

    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Box {
            OutlinedButton(
                onClick = { eventMenu = true },
                modifier = if (compact) Modifier.fillMaxWidth() else Modifier.widthIn(min = 360.dp),
                shape = RoundedCornerShape(10.dp),
            ) {
                Text(eventLabel, color = AdminColors.Text)
            }
            DropdownMenu(expanded = eventMenu, onDismissRequest = { eventMenu = false }) {
                AdminNotificationEventCode.entries.forEach { event ->
                    DropdownMenuItem(
                        text = { Text(event.labelEs()) },
                        onClick = {
                            onSelectEvent(event)
                            eventMenu = false
                        },
                    )
                }
            }
        }
        if (variablesHelp.isNotBlank()) {
            Text(variablesHelp.trim(), color = AdminColors.TextMuted, fontSize = 12.sp)
        }
        TemplateField("Asunto del correo", subject, onSubject, singleLine = true)
        TemplateField("Cuerpo del correo", emailBody, onEmailBody, minLines = 5)
        TemplateField("Título en panel web", webTitle, onWebTitle, singleLine = true)
        TemplateField("Mensaje en panel web", webBody, onWebBody, minLines = 3)
        Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            Button(
                onClick = onSave,
                enabled = !saving,
                shape = RoundedCornerShape(10.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = AdminColors.Primary,
                    contentColor = AdminColors.OnBrand,
                ),
            ) { Text(if (saving) "…" else "Guardar plantilla", fontWeight = FontWeight.SemiBold) }
            TextButton(onClick = onReset, enabled = !saving) {
                Text("Restaurar predeterminada", color = AdminColors.Primary)
            }
        }
    }
}

@Composable
private fun TemplateField(
    label: String,
    value: String,
    onChange: (String) -> Unit,
    singleLine: Boolean = false,
    minLines: Int = 1,
) {
    Column {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(4.dp))
        OutlinedTextField(
            value = value,
            onValueChange = onChange,
            modifier = Modifier.fillMaxWidth(),
            singleLine = singleLine,
            minLines = if (singleLine) 1 else minLines,
            shape = RoundedCornerShape(10.dp),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = AdminColors.Primary,
                unfocusedBorderColor = AdminColors.Outline,
            ),
        )
    }
}

@Composable
private fun ChannelCheck(label: String, checked: Boolean, onChange: (Boolean) -> Unit) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Checkbox(
            checked = checked,
            onCheckedChange = onChange,
            colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
        )
        Text(label, color = AdminColors.Text, fontSize = 13.sp)
    }
}

@Composable
private fun NotificationTimingSection(
    schedules: List<AdminSettingsNotificationSchedule>,
    compact: Boolean,
    onUpdate: (
        AdminNotificationEventCode,
        (AdminSettingsNotificationSchedule) -> AdminSettingsNotificationSchedule,
    ) -> Unit,
    saving: Boolean,
    onSave: () -> Unit,
) {
    Text("Cuándo se envían", fontWeight = FontWeight.Bold, fontSize = 15.sp)
    Text(
        "Los avisos de pago, alta de cliente, reservas, etc. salen al instante. " +
            "Aquí configuras la regularidad de las notificaciones programadas.",
        color = AdminColors.TextMuted,
        fontSize = 12.sp,
        modifier = Modifier.padding(top = 4.dp, bottom = 10.dp),
    )

    InstantEventsHint()

    Spacer(Modifier.height(12.dp))

    if (schedules.isEmpty()) {
        Text(
            "Aún no hay horarios programados. Guarda para crear el de «Membresía por vencer».",
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
        )
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            schedules.forEach { schedule ->
                ScheduleCard(
                    schedule = schedule,
                    compact = compact,
                    onChange = { updated -> onUpdate(schedule.eventCode) { updated } },
                )
            }
        }
        Spacer(Modifier.height(12.dp))
        Button(
            onClick = onSave,
            enabled = !saving && schedules.isNotEmpty(),
            modifier = if (compact) Modifier.fillMaxWidth().height(44.dp) else Modifier,
            shape = RoundedCornerShape(10.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = AdminColors.Primary,
                contentColor = AdminColors.OnBrand,
            ),
        ) {
            Text(if (saving) "…" else "Guardar horarios", fontWeight = FontWeight.SemiBold)
        }
        Spacer(Modifier.height(8.dp))
        Text(
            "El recordatorio WhatsApp de planes semanales (sáb/dom) se configura en la pestaña WhatsApp. Solo informa; no crea órdenes.",
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
    }
}

@Composable
private fun InstantEventsHint() {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.Outline.copy(alpha = 0.12f))
            .padding(12.dp),
    ) {
        Text("Al instante (cuando ocurre)", fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
        Spacer(Modifier.height(4.dp))
        Text(
            "Nuevo cliente · Pagos · Reservas · Ventas · Stock bajo · Mensajes",
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
    }
}

@Composable
private fun ScheduleCard(
    schedule: AdminSettingsNotificationSchedule,
    compact: Boolean,
    onChange: (AdminSettingsNotificationSchedule) -> Unit,
) {
    var hourMenu by remember(schedule.eventCode) { mutableStateOf(false) }
    val leadSelected = remember(schedule.leadDays) {
        schedule.leadDays.orEmpty().split(",", ";", " ")
            .mapNotNull { it.trim().toIntOrNull() }
            .toSet()
    }

    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(14.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.45f), RoundedCornerShape(14.dp))
            .background(AdminColors.Surface)
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(Modifier.weight(1f)) {
                Text(schedule.eventLabel, fontWeight = FontWeight.SemiBold, fontSize = 14.sp)
                Text(
                    when (schedule.frequency) {
                        AdminNotificationScheduleFrequency.DAILY ->
                            "Todos los días a las ${schedule.sendHour.toString().padStart(2, '0')}:00 (${schedule.timezone})"
                        AdminNotificationScheduleFrequency.WEEKLY ->
                            "Solo algunos días · ${schedule.sendHour.toString().padStart(2, '0')}:00"
                        AdminNotificationScheduleFrequency.INSTANT -> "Al instante"
                    },
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                Checkbox(
                    checked = schedule.enabled,
                    onCheckedChange = { onChange(schedule.copy(enabled = it)) },
                    colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                )
                Text("Activo", fontSize = 12.sp, color = AdminColors.Text)
            }
        }

        Text("Regularidad", color = AdminColors.TextMuted, fontSize = 12.sp)
        Row(
            Modifier.horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            listOf(
                AdminNotificationScheduleFrequency.DAILY,
                AdminNotificationScheduleFrequency.WEEKLY,
            ).forEach { freq ->
                FrequencyChip(
                    label = freq.labelEs(),
                    selected = schedule.frequency == freq,
                    onClick = { onChange(schedule.copy(frequency = freq, frequencyLabel = freq.labelEs())) },
                )
            }
        }

        Text("Hora de envío", color = AdminColors.TextMuted, fontSize = 12.sp)
        Box {
            OutlinedButton(onClick = { hourMenu = true }) {
                Text("${schedule.sendHour.toString().padStart(2, '0')}:00")
            }
            DropdownMenu(expanded = hourMenu, onDismissRequest = { hourMenu = false }) {
                (0..23).forEach { h ->
                    DropdownMenuItem(
                        text = { Text("${h.toString().padStart(2, '0')}:00") },
                        onClick = {
                            onChange(schedule.copy(sendHour = h))
                            hourMenu = false
                        },
                    )
                }
            }
        }

        if (schedule.frequency == AdminNotificationScheduleFrequency.WEEKLY) {
            Text("Días de la semana", color = AdminColors.TextMuted, fontSize = 12.sp)
            Row(
                Modifier.horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                DayChip("Lun", schedule.mondayEnabled) {
                    onChange(schedule.copy(mondayEnabled = it))
                }
                DayChip("Mar", schedule.tuesdayEnabled) {
                    onChange(schedule.copy(tuesdayEnabled = it))
                }
                DayChip("Mié", schedule.wednesdayEnabled) {
                    onChange(schedule.copy(wednesdayEnabled = it))
                }
                DayChip("Jue", schedule.thursdayEnabled) {
                    onChange(schedule.copy(thursdayEnabled = it))
                }
                DayChip("Vie", schedule.fridayEnabled) {
                    onChange(schedule.copy(fridayEnabled = it))
                }
                DayChip("Sáb", schedule.saturdayEnabled) {
                    onChange(schedule.copy(saturdayEnabled = it))
                }
                DayChip("Dom", schedule.sundayEnabled) {
                    onChange(schedule.copy(sundayEnabled = it))
                }
            }
        }

        if (schedule.eventCode == AdminNotificationEventCode.MEMBERSHIP_EXPIRING) {
            Text(
                "Avisar cuando falten estos días para el vencimiento",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
            Row(
                Modifier.horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                listOf(1, 3, 7, 15, 30).forEach { day ->
                    val selected = day in leadSelected
                    FrequencyChip(
                        label = if (day == 1) "1 día" else "$day días",
                        selected = selected,
                        onClick = {
                            val next = if (selected) leadSelected - day else leadSelected + day
                            val sorted = next.sorted().joinToString(",")
                            onChange(schedule.copy(leadDays = sorted.ifBlank { "7,3,1" }))
                        },
                    )
                }
            }

            Text(
                "Pasar a inactivo al cliente después del vencimiento",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
            Row(
                Modifier.horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                listOf(0 to "No", 30 to "30 días", 90 to "90 días").forEach { (dias, label) ->
                    FrequencyChip(
                        label = label,
                        selected = schedule.inactivateAfterDays == dias,
                        onClick = { onChange(schedule.copy(inactivateAfterDays = dias)) },
                    )
                }
            }
            if (schedule.inactivateAfterDays > 0) {
                Text(
                    "Al vencer y pasar ${schedule.inactivateAfterDays} días sin renovar, el cliente queda inactivo y sale del terminal. " +
                        "Los trabajadores se inactivan a mano.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
        }
    }
}

@Composable
private fun FrequencyChip(label: String, selected: Boolean, onClick: () -> Unit) {
    Text(
        label,
        modifier = Modifier
            .clip(RoundedCornerShape(20.dp))
            .background(if (selected) AdminColors.Primary.copy(alpha = 0.18f) else AdminColors.Outline.copy(alpha = 0.15f))
            .border(
                1.dp,
                if (selected) AdminColors.Primary else AdminColors.Outline.copy(alpha = 0.4f),
                RoundedCornerShape(20.dp),
            )
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp),
        color = if (selected) AdminColors.Primary else AdminColors.Text,
        fontSize = 12.sp,
        fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
    )
}

@Composable
private fun DayChip(label: String, selected: Boolean, onChange: (Boolean) -> Unit) {
    Text(
        label,
        modifier = Modifier
            .clip(RoundedCornerShape(20.dp))
            .background(if (selected) AdminColors.Primary.copy(alpha = 0.18f) else AdminColors.Outline.copy(alpha = 0.15f))
            .border(
                1.dp,
                if (selected) AdminColors.Primary else AdminColors.Outline.copy(alpha = 0.4f),
                RoundedCornerShape(20.dp),
            )
            .clickable { onChange(!selected) }
            .padding(horizontal = 10.dp, vertical = 8.dp),
        color = if (selected) AdminColors.Primary else AdminColors.Text,
        fontSize = 12.sp,
        fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
    )
}

